import {
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
} from '@floating-ui/dom';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { PlainLink } from 'components/link';
import useClickOutside from 'lib/hooks/useClickOutside';

import styles from './participation-links.module.scss';

interface Props {
  workspaces: Array<{ id: number; name: string }>;
  // Builds the destination for a workspace: its Runsheet, filtered to the entity.
  runsheetHref: (workspaceId: number) => string;
}

// The "Title Workspaces: N" count opens this popup listing each workspace so the
// user can weigh the names before navigating. Each name opens that workspace's
// Runsheet (filtered to the entity) in a new tab, ordered alphabetically.
//
// Portalled to the body and positioned with floating-ui, following the
// FloatingTooltip primitive: the trigger sits in a scrollable table cell, so an
// absolutely-positioned panel would be clipped by the table and the last row's
// popup would be unreachable without scrolling.
export const TitleWorkspacesPopover = ({ workspaces, runsheetHref }: Props) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLUListElement>(null);

  // The panel lives outside the trigger's subtree, so it needs its own ref here
  // or every click inside it would read as an outside click and close the popup.
  useClickOutside({
    refs: [triggerRef, panelRef],
    onClickOutside: () => setOpen(false),
  });

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);

    let stop: (() => void) | undefined;
    const frame = requestAnimationFrame(() => {
      if (!triggerRef.current || !panelRef.current) return;
      stop = autoUpdate(triggerRef.current, panelRef.current, () => {
        if (!triggerRef.current || !panelRef.current) return;
        computePosition(triggerRef.current, panelRef.current, {
          placement: 'bottom-start',
          strategy: 'fixed',
          middleware: [offset(4), flip(), shift({ padding: 8 })],
        }).then(({ x, y }) => {
          if (!panelRef.current) return;
          Object.assign(panelRef.current.style, {
            left: `${x}px`,
            top: `${y}px`,
            visibility: 'visible',
          });
        });
      });
    });

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      cancelAnimationFrame(frame);
      stop?.();
    };
  }, [open]);

  const ordered = [...workspaces].sort((a, b) => a.name.localeCompare(b.name));

  // The trigger and its links sit inside a row that navigates on click, so every
  // interaction here stops propagation or the row handler hijacks it.
  const swallowRowClick = (event: React.MouseEvent) => event.stopPropagation();

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        // A disclosure, not a menu: the panel is a list of navigable links
        // (each opens a workspace runsheet in a new tab), so Tab moves through
        // them naturally. Declaring role="menu"/"menuitem" would promise roving
        // arrow-key navigation and focus management this doesn't implement.
        aria-haspopup="true"
        aria-expanded={open}
        onClick={(event) => {
          swallowRowClick(event);
          setOpen((isOpen) => !isOpen);
        }}
      >
        Title Workspaces: {workspaces.length}
      </button>
      {open &&
        createPortal(
          <ul
            ref={panelRef}
            className={styles.popover}
            aria-label="Title workspaces"
          >
            {ordered.map((workspace) => (
              <li key={workspace.id}>
                <PlainLink
                  className={styles.popoverLink}
                  href={runsheetHref(workspace.id)}
                  openInNewTab
                  onClick={swallowRowClick}
                >
                  {workspace.name}
                </PlainLink>
              </li>
            ))}
          </ul>,
          document.body
        )}
    </>
  );
};
